import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Loader2 } from "lucide-react"
import { useAuth } from "../../../contexts/AuthContext"
import { updateUserPreferences } from "../../../lib/api/api"
import ChangePasswordForm from "./ChangePasswordForm"
import { CANTON_CODES } from "../../../shared/cantons"

// Every canton in the Confederation: only the current session's user can
// change their own preferred one, used to filter cantonal search lanes.
// Names are proper nouns in the UI language: they live in it.json under
// `cantons.<code>` so subproject 4 can translate them.
export default function ProfileSecurityTab() {
  const { t } = useTranslation()
  const { user, refreshUser } = useAuth()
  const cantons = useMemo(
    () => [...CANTON_CODES].sort().map((code) => ({ code, name: t(`cantons.${code}`) })),
    [t]
  )

  const [canton, setCanton] = useState<string>(user?.preferredCanton || 'TI')
  const [savingCanton, setSavingCanton] = useState(false)
  const [cantonError, setCantonError] = useState<string | null>(null)

  // Keep the local canton in sync if the user is loaded after mount.
  useEffect(() => {
    if (user?.preferredCanton) {
      setCanton(user.preferredCanton)
    }
  }, [user?.preferredCanton])

  const handleCantonChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCanton = e.target.value
    const previousCanton = canton
    setCantonError(null)
    setSavingCanton(true)
    // Optimistic local update
    setCanton(newCanton)
    try {
      await updateUserPreferences({ preferred_canton: newCanton })
      // Re-sync AuthContext so other components and a later visit to this
      // page see the new value without requiring a full page reload.
      await refreshUser()
    } catch (err) {
      console.error('Failed to update preferred canton:', err)
      setCanton(previousCanton)
      setCantonError(t('settings.profile.cantonError'))
    } finally {
      setSavingCanton(false)
    }
  }

  return (
    <div className="space-y-10">
      <div className="max-w-2xl">
        <h2 className="text-xl font-semibold mb-4">{t('settings.profile.title')}</h2>
        <div className="my-4">
          <label htmlFor="preferred-canton" className="block text-sm font-medium text-gray-700 mb-1">
            {t('settings.profile.cantonLabel')}
          </label>
          <p className="text-xs text-gray-500 mb-2">{t('settings.profile.cantonHelp')}</p>
          <select
            id="preferred-canton"
            value={canton}
            onChange={handleCantonChange}
            disabled={savingCanton}
            className="block w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            {cantons.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
          {savingCanton && (
            <p className="mt-2 text-xs text-gray-500 flex items-center">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              {t('settings.profile.saving')}
            </p>
          )}
          {cantonError && (
            <p className="mt-2 text-xs text-red-600">{cantonError}</p>
          )}
        </div>
      </div>

      <div className="max-w-2xl border-t border-gray-200 pt-8">
        <ChangePasswordForm email={user?.email} />
      </div>
    </div>
  )
}
